// Rotate / spin / flip drawn conditions — the pure part (the OST "Rotate/Flip" menu: Rotate Takeoff Ctrl+R, Spin
// Takeoff in Place Ctrl+S, Flip Horizontal / Vertical). Acts on what a person drew; the save (one undo step, room
// re-detected) is RoomTakeoff's. Coordinates are world feet with y DOWN, so a positive angle turns clockwise on screen.
//
// op = { type: 'rotate', deg } | { type: 'flipH' } | { type: 'flipV' } | { type: 'translate', dx, dy } | { type: 'scale', factor, pivot? }
//   translate / scale  the double-click edit mode: drag a shape somewhere else, or drag its corner handle to make it larger
//           or smaller (uniform about `pivot`, else the group's centre); a symbol's angles are left alone
//   rotate  turns every point about the pivot; a placed symbol's angle (angleDeg / wallNormalDeg) turns with it
//   flipH   mirrors left-right about the pivot's x; flipV mirrors top-bottom about its y — a symbol's angles are
//           mirrored and its hinge side (`flip`) toggles, since a mirror reverses handedness
// An area's stored tile-grid result is dropped: the grid is anchored at the shape's bounding box, which a turn changes,
// so the canvas's tile-count sync recomputes it.
import { pointsOfRow, copyableRows } from "./conditionClipboard";

const geometryOf = (json) => { try { const g = typeof json === "string" ? JSON.parse(json || "{}") : json; return g || {}; } catch { return {}; } };
const r2 = (v) => Math.round(v * 100) / 100;
const norm = (deg) => { const d = ((deg % 360) + 360) % 360; return Math.round(d * 100) / 100; };

export const ROTATE_STEP_DEG = 90;

/** Centre of the bounding box of every point of these rows, or null. */
export function selectionPivot(rows) {
  const pts = (rows || []).flatMap(pointsOfRow);
  if (!pts.length) return null;
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
  return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
}

function movePoint(p, op, pivot) {
  if (op.type === "translate") return { x: p.x + op.dx, y: p.y + op.dy };
  if (op.type === "scale") return { x: pivot.x + (p.x - pivot.x) * op.factor, y: pivot.y + (p.y - pivot.y) * op.factor };
  if (op.type === "flipH") return { x: 2 * pivot.x - p.x, y: p.y };
  if (op.type === "flipV") return { x: p.x, y: 2 * pivot.y - p.y };
  const a = (op.deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  const dx = p.x - pivot.x, dy = p.y - pivot.y;
  return { x: pivot.x + dx * c - dy * s, y: pivot.y + dx * s + dy * c };
}

function moveAngle(deg, op) {
  if (op.type === "flipH") return norm(180 - deg);
  if (op.type === "flipV") return norm(-deg);
  return norm(deg + op.deg);
}

/** GeometryJson transformed by `op` about `pivot`; everything not geometric (width, tile settings…) is kept. */
export function transformGeometryJson(geometryJson, op, pivot) {
  const g = geometryOf(geometryJson);
  const out = { ...g };
  if (Array.isArray(g.points)) {
    out.points = g.points.map(p => { const q = movePoint({ x: +p.x, y: +p.y }, op, pivot); return { ...p, x: r2(q.x), y: r2(q.y) }; });
    delete out.grid;
  } else if (Number.isFinite(+g.x) && Number.isFinite(+g.y)) {
    const q = movePoint({ x: +g.x, y: +g.y }, op, pivot);
    out.x = r2(q.x); out.y = r2(q.y);
    // translate / scale never turn or mirror a symbol
    if (op.type !== "translate" && op.type !== "scale") {
      if (g.angleDeg != null) out.angleDeg = moveAngle(+g.angleDeg || 0, op);
      else if (op.type === "rotate") out.angleDeg = moveAngle(0, op);
      if (g.wallNormalDeg != null) out.wallNormalDeg = moveAngle(+g.wallNormalDeg || 0, op);
      if (op.type !== "rotate") out.flip = !g.flip;
    }
  }
  return JSON.stringify(out);
}

/**
 * What to change for these rows: { changes: [{ row, geometryJson }], skipped }.
 * spin = false → one shared pivot (the group's centre, so the layout turns as a unit);
 * spin = true  → each shape about its own centre ("spin in place").
 */
export function planTransform(rows, op, { spin = false } = {}) {
  const { rows: ok, skipped } = copyableRows(rows);
  const shared = spin ? null : selectionPivot(ok);
  const changes = ok.map(row => {
    const pivot = op.pivot || (spin ? selectionPivot([row]) : shared);
    return { row, geometryJson: transformGeometryJson(row.GeometryJson, op, pivot) };
  });
  return { changes, skipped };
}

/** "Rotated 3 conditions" / "…(2 generated left out)". */
export function transformNotice(verb, count, skipped = {}) {
  const left = [];
  if (skipped.machine) left.push(`${skipped.machine} generated`);
  if (skipped.backOut) left.push(`${skipped.backOut} back-out${skipped.backOut === 1 ? "" : "s"}`);
  if (skipped.noGeometry) left.push(`${skipped.noGeometry} with no drawn shape`);
  const base = count ? `${verb} ${count} condition${count === 1 ? "" : "s"}` : `Nothing to ${verb.toLowerCase().replace(/ed$/, "").replace(/ipp$/, "ip")}`;
  return left.length ? `${base} (${left.join(", ")} left out)` : base;
}
